import React, { useEffect, useState } from 'react';
import { FieldGroup } from '@ver-uds/react';
import { Alert, Form } from '@ver-uds/uswds-react';
import { FormProvider, useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { Account, AccountType } from 'eve-account-client';
import { EveRoutes } from '@ver-uds/e-verify';
import FullWidthLayout from '../layouts/FullWidthLayout';
import AlertConstants from '../constants/AlertConstants';
import HelpTextConstants from '../constants/HelpTextConstants';
import { useClosedReasonsQuery } from '../services/RefDataClosedReasonsApi';
import SelectedCompanyLocationsAccordion from './ManageCompanies/SelectedCompanyLocationsAccordion';
import { getVerbiageForTerminationRequest } from '../utils/termination-request';
import TerminationRequestFeedback from './TerminationRequestFeedback';

const todayDatePretty = format(new Date(), 'E, MMM d, yyyy');

type TerminationRequestProps = {
  accountType: AccountType;
  selectedAccounts: Array<Account>;
  terminationFormValues: FormValues;
  goToReviewPage: (terminationFormValues: FormValues) => void;
};

type FormValues = {
  closedReasonCode: string;
  closedReasonText: string;
  childClosedReasonCode: string;
  dunsNumber: string;
};

const TerminateCompanyLocations = ({
  accountType,
  selectedAccounts,
  terminationFormValues,
  goToReviewPage,
}: TerminationRequestProps): JSX.Element => {
  const [terminationAllowed, setTerminationAllowed] = useState(true);
  let manageCompaniesLink: string;
  let manageCompaniesLinkText: string;
  let headerTitle: string;
  let pageTitle: string;
  let accountTypeForClosedReasons: AccountType;

  if (accountType === AccountType.AGENCY) {
    manageCompaniesLink = EveRoutes.COMPANY_CLIENTS_VIEW;
    manageCompaniesLinkText = 'Manage Client Companies';
    headerTitle = 'Client Companies';
    pageTitle = `Terminate ${headerTitle}`;

    // because this component is only used for the company locations/child accounts, this won't affect the
    // closed reasons for the account itself
    accountTypeForClosedReasons = AccountType.CLIENT;
  } else {
    manageCompaniesLink = EveRoutes.CORPORATE_COMPANY_LOCATIONS_VIEW;
    manageCompaniesLinkText = 'Manage Company Locations';
    headerTitle = 'Company Locations';
    pageTitle = `Terminate ${headerTitle}`;
    accountTypeForClosedReasons = accountType;
  }

  const { data: closedReasons } = useClosedReasonsQuery(accountTypeForClosedReasons);

  const { companyText, companyPronoun, companyPronoun2, verb } = getVerbiageForTerminationRequest(
    selectedAccounts.length,
  );

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const methods = useForm<FormValues>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: terminationFormValues || {
      closedReasonCode: undefined,
      closedReasonText: undefined,
      childClosedReasonCode: undefined,
    },
  });

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { watch, handleSubmit } = methods;

  const { closedReasonCode, closedReasonText, childClosedReasonCode } = watch();

  // Find selected reasons list
  const matchingReasonsList = closedReasons
    ?.filter((reason) => reason.code === (childClosedReasonCode ?? closedReasonCode))
    .flatMap((reason) => ({
      terminationNotAllowed: reason.terminationNotAllowed,
      terminationNotAllowedReason: reason.terminationNotAllowedReason,
    }));

  const selectedReason = matchingReasonsList && matchingReasonsList.length > 0 ? matchingReasonsList[0] : null;
  const onFormSubmit = handleSubmit(() => {
    if (selectedReason && selectedReason.terminationNotAllowed) {
      // If termination is not required, display banner
      setTerminationAllowed(false);
    } else {
      // Continue to review screen
      const formValues: FormValues = {
        closedReasonCode,
        closedReasonText,
        childClosedReasonCode,
        dunsNumber: '',
      };
      goToReviewPage(formValues);
    }
  });

  const alertText = AlertConstants.TERMINATION_COMPANY_LOCATIONS.replace('{{ companyPronoun }}', companyPronoun)
    .replace('{{ companyText }}', companyText)
    .replace('{{ companyPronoun2 }}', companyPronoun2)
    .replace('{{ verb }}', verb);

  const feedbackText = HelpTextConstants.TERMINATION_REQUEST.replace(
    '{{ companyText }}',
    `${companyPronoun} ${companyText}`,
  );

  // Reset termination required if reason is changed
  useEffect(() => {
    setTerminationAllowed(true);
  }, [closedReasonCode, childClosedReasonCode]);

  return (
    <FullWidthLayout title={pageTitle} headerTitle={headerTitle}>
      <section className="padding-y-4">
        <h1>{pageTitle}</h1>

        {terminationAllowed && (
          <Alert className="margin-top-4" status="warning" slim>
            <Alert.Text>{alertText}</Alert.Text>
          </Alert>
        )}
        <div className="margin-top-4 margin-bottom-3">
          <SelectedCompanyLocationsAccordion selectedAccounts={selectedAccounts} />
        </div>

        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <FormProvider {...methods}>
          <Form className="margin-top-3 margin-bottom-3 maxw-none" onSubmit={onFormSubmit} noValidate>
            <FieldGroup>
              <FieldGroup.Field className="tablet:grid-col-12 margin-top-3" name="Termination Request Date">
                {todayDatePretty}
              </FieldGroup.Field>
            </FieldGroup>

            <TerminationRequestFeedback
              terminationAllowed={terminationAllowed}
              terminationNotAllowedReason={selectedReason?.terminationNotAllowedReason}
              feedbackText={feedbackText}
              closedReasons={closedReasons}
              link={manageCompaniesLink}
              linkText={manageCompaniesLinkText}
            />
          </Form>
        </FormProvider>
      </section>
    </FullWidthLayout>
  );
};

export default TerminateCompanyLocations;
